import Vue from 'vue';
import Router, { NavigationGuard, RouteConfig } from 'vue-router';

import {
    configQuery,
    getFeatureEnabled,
    getFeatureValueV2,
    ipCountryQuery,
    isMobileQuery,
    ModalSize,
    setRouteGetter,
} from '@gig/magic-common';
import { magic } from '@gig/magic-sdk';

import {
    config,
    PassThrough,
    initAuthenticationGuard,
    initConsentRequiredGuard,
    initReturningUserGuard,
    allowWithPendingConsents,
    authenticationGuard,
    getRoute,
    AuthRestriction,
    lightTopNavigation,
    zendeskChat,
    initGracePeriodGuard,
    initSGASetLimitsGuard,
    initSgaIncompleteGuard,
} from '../services';
import {
    modalGuard,
    initModalGuard,
    initNavigationGuard,
    initLanguageGuard,
    initBettingLimitsGuard,
    initVerificationGuard,
    allowPendingVerification,
    initAgcoFirstLoginGuard,
    initAglcFirstLoginGuard,
} from '../services';

// Pages
import MessagesPage from './messages/messages.vue';
import NotFoundPage from './404/404.vue';
const SportsbookPage = () =>
    import(/* webpackChunkName: "sportsbook" */ './sportsbook/sportsbook.vue');
// Route Configuration
import { AccountRoutes } from './account/account.routes';
import { WalletRoutes } from './wallet/wallet.routes';
import { FaqRoutes } from './pages/faq/faq.routes';
import { RewardRoutes } from './rewards/reward.routes';
import {
    getGamificationComponent,
    PromotionsRoutes,
} from './promotions/promotion.routes';
import { TournamentsRoutes } from './tournaments/tournaments.routes';
import { LandingV2Routes } from './landing/landing-v2.routes';
import { playForFunGuard } from '../services/routing/guards/play-for-fun.guard';
import {
    initSowGuard,
    allowWithPendingGracePeriod,
    initFeatureGuard,
    featureGuard,
    initAccountLinkingGuard,
    initSubdivisionGuard,
    initPepGuard,
    initVerifyDetailsModalGuard,
} from '../services';
import { RouteName } from './routes.constants';
import { Feature, openProperLiveChat } from '../util';
import {
    requireDepositLimitsGuard,
    initDepositLimitGuard,
} from '../services/routing/guards/deposit-limits.guard';
import {
    initPCPreferencesGuard,
    initPlayControlGuard,
} from '../services/routing/guards/play-control';
import { initResponsibleGamingGuard } from '../services/routing/guards/responsible-gaming';
import { Jurisdiction } from '@gig/magic-jurisdictions';
import { toolkit } from '@gig/magic-frontend-toolkit';
import { initCMSFeaturesQuery } from '../services/routing/helpers/feature-query';
import {
    FEATURE_QUERY_ENABLED,
    SINGLE_LANGUAGE_QUERY,
    WINDOW_BEFORE_UNLOAD,
} from '@gig/magic-messages';
import { getGamesComponent } from './games/games.routes';
import { getRGQuestionnaireComponent } from './rg-questionnaire/rg-questionnaire.routes';
import { getBingoComponent } from './bingo/bingo.routes';
import { getPagesComponent } from './pages/pages.routes';
import { initAllowedCountries } from '../services/routing/guards/allowed-countries.guard';
import { initConfirmationLimitsGuard } from '../services/routing/guards/confirmation-limits.guard';
import PersonalisedOfferPage from './personalised-offers/personalised-offers.vue';

import { getHomePageComponent } from './home-page/home-page.routes';
import { initTimedOutGuard } from '../services/routing/guards/timed-out.guard';
import { store } from '../store';

Vue.use(Router);

const openLiveChat: NavigationGuard = (_, from, next) => {
    openProperLiveChat();
    if (from.matched.length === 0) {
        // If coming from outside, go home
        next('/');
    } else {
        // Else just open the chat and keep current page open
        next(false);
    }
};

const oldLotbaSelfExcludeLink: NavigationGuard = async (_, from, next) => {
    const isLoggedIn = !!magic.session.getUser();
    if (isLoggedIn) {
        try {
            await magic.methods.users.logout();
        } catch (e) {
            console.error(e);
        }
    } else {
        next(from.fullPath);
    }
    window.open('https://saberjugar.gob.ar/?page=autoexlusion', '_blank');
};

const onGameplayOpen: NavigationGuard = async (to, from, next) => {
    const mobile = await isMobileQuery();
    if (!mobile || !from.name) {
        return next();
    }

    const reloadEnabled = await getFeatureEnabled(Feature.GAMEPLAY_RELOAD);
    if (!reloadEnabled) {
        return next();
    }

    const games = await getFeatureValueV2<string>(Feature.GAMEPLAY_RELOAD);
    if (!games) {
        return next();
    }

    if (!games.split(',').includes(to.params.gameSlug)) {
        return next();
    }

    window.location.href = to.fullPath;
};

function getSingleLanguageRoutes(children: RouteConfig[]) {
    return [
        {
            path: '/',
            meta: {
                ...allowWithPendingConsents(),
            },
            component: PassThrough,
            children,
        },
    ];
}

function getMultiLanguageRoutes(children: RouteConfig[]) {
    return [
        {
            path: '/',
            meta: {
                ...allowWithPendingConsents(),
            },
        },
        { path: '/:language', component: PassThrough, children },
    ];
}

let router: Router | undefined;
export async function getRouter(): Promise<Router> {
    if (router) {
        return Promise.resolve(router);
    }

    const ipCountry = await ipCountryQuery();
    const isForceLoginForGamesEnabled = await window.queryBus.query<
        string,
        boolean
    >(FEATURE_QUERY_ENABLED, 'require-login-for-game-launch');
    const singleLanguage: boolean = await window.queryBus.query(
        SINGLE_LANGUAGE_QUERY,
    );

    const isHomePage = await getFeatureEnabled(Feature.DEDICATED_HOMEPAGE);

    const isSportsbookOnly = await configQuery<Record<string, boolean>>(
        'isSportsbookOnly',
    );

    const sportsbookEnabled = await getFeatureEnabled(Feature.SPORTSBOOK);
    const sportsbookHomepageEnabled = await getFeatureEnabled(
        Feature.SPORTSBOOK_HOMEPAGE,
    );

    const resolveHomePageComponent = () => {
        if (
            isSportsbookOnly ||
            (sportsbookEnabled && sportsbookHomepageEnabled)
        ) {
            return SportsbookPage;
        }

        if (isHomePage) {
            return getHomePageComponent('HomePage');
        }

        return getGamesComponent('GamesPage');
    };

    const children: RouteConfig[] = [
        {
            name: RouteName.LIVE_CHAT,
            path: 'live-chat',
            beforeEnter: openLiveChat,
            meta: {
                timedOutAllowed: true,
                ...allowWithPendingGracePeriod(),
                ...allowWithPendingConsents(),
                ...allowPendingVerification(),
            },
        },
        {
            name: RouteName.HOME,
            path: '',
            component: resolveHomePageComponent(),
            meta: {
                timedOutAllowed: true,
                ...allowWithPendingConsents(),
                ...allowPendingVerification(),
            },
        },
        {
            name: RouteName.GAMES,
            path: 'games/:categorySlug?',
            component: getGamesComponent('GamesPage'),
            meta: {
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.PERSONALISED_OFFER,
            path: 'personalised-offer/:slug',
            component: PersonalisedOfferPage,
            meta: {
                ...modalGuard(
                    () => PersonalisedOfferPage,
                    'personalised-offers-modal.title',
                ),
                ...lightTopNavigation('personalised-offers-modal.title'),
            },
        },
        {
            name: RouteName.PROVIDERS,
            path: 'providers/:slug?',
            component: getGamesComponent('GamesPage'),
            meta: {
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.LIVE_CASINO,
            path: 'live-casino/:categorySlug?',
            component: getGamesComponent('GamesPage'),
            meta: {
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.FREE_TO_PLAY,
            path: 'free-to-play',
            component: getGamesComponent('FreeToPlayPage'),
            meta: {
                ...featureGuard(Feature.FREE_TO_PLAY_LOBBY),
            },
        },
        {
            name: RouteName.PROMOTION_PLAY,
            path: 'free-to-play/:promotionSlug',
            component: getGamesComponent('PromotionPlay'),
            meta: {
                alwaysScrollToTop: true,
                ...playForFunGuard(ipCountry),
                ...(isForceLoginForGamesEnabled
                    ? authenticationGuard(AuthRestriction.AUTH_REQUIRED)
                    : {}),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.LOYALTY_PROGRAM,
            path: 'loyalty-program',
            component: getGamificationComponent('LoyaltyProgramPage'),
            meta: {
                ...featureGuard(Feature.LOYALTY_PROGRAM),
                ...lightTopNavigation('loyalty-program.title'),
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
            },
        },
        {
            name: RouteName.BINGO_LOBBY,
            path: 'bingo-lobby/:gameId?',
            component: getBingoComponent('BingoPage'),
            meta: {
                ...featureGuard(Feature.BINGO_LOBBY),
                ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
            },
        },
        {
            name: RouteName.GAMEPLAY,
            path: `:lobby(${config.lobbiesPath.join(
                '|',
            )})/:categorySlug/:gameSlug`,
            beforeEnter: onGameplayOpen,
            component: getGamesComponent('GameplayPage'),
            meta: {
                alwaysScrollToTop: true,
                ...playForFunGuard(ipCountry),
                ...(isForceLoginForGamesEnabled
                    ? authenticationGuard(AuthRestriction.AUTH_REQUIRED)
                    : {}),
                ...requireDepositLimitsGuard(
                    config.gamePlay.depositLimitsRequiredCountries,
                ),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            path: 'account/',
            component: PassThrough,
            children: AccountRoutes,
        },
        {
            path: 'rewards',
            component: PassThrough,
            children: RewardRoutes,
            meta: {
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            path: 'promotions',
            component: PassThrough,
            meta: {
                ...featureGuard(Feature.PROMOTIONS),
                ...lightTopNavigation('promotions.title'),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
            children: PromotionsRoutes,
        },
        {
            path: 'landing',
            component: PassThrough,
            meta: {
                ...lightTopNavigation('landing.title'),
                shouldHideMainMenu: false,
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
            children: LandingV2Routes,
        },
        {
            path: 'tournaments',
            component: PassThrough,
            meta: {
                ...featureGuard(Feature.TOURNAMENTS),
                ...lightTopNavigation('tournaments.title'),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
            children: TournamentsRoutes,
        },
        {
            name: RouteName.LEADERBOARDS,
            path: 'leaderboard/:competitionSlug/:page?',
            component: getGamificationComponent('LeaderboardsPage'),
            meta: {
                ...featureGuard(Feature.LEADERBOARDS),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.SEARCH,
            path: 'search',
            component: getGamesComponent('SearchPage'),
            meta: {
                ...lightTopNavigation('search.title'),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            path: 'wallet',
            component: PassThrough,
            children: WalletRoutes,
            meta: {
                ...allowWithPendingGracePeriod(),
            },
        },
        {
            name: RouteName.MESSAGES,
            path: 'messages/:messageId?',
            component: MessagesPage,
            meta: {
                shouldDisableRGAlerts: true,
                ...featureGuard(Feature.MESSAGES),
                ...modalGuard(() => MessagesPage, 'messages.title'),
                ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
                ...lightTopNavigation('messages.title'),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.TNC,
            path: 'tnc',
            component: getPagesComponent('TncPage'),
            meta: {
                shouldDisableRGAlerts: true,
                ...allowWithPendingConsents(),
                ...allowWithPendingGracePeriod(),
                ...allowPendingVerification(),
                ...modalGuard(getPagesComponent('TncPage'), 'tnc.title', {
                    uniqueModalId: 'terms',
                }),
                ...lightTopNavigation('tnc.title'),
            },
        },
        {
            name: RouteName.BONUS_TERMS,
            path: 'customer-care',
            component: getPagesComponent('BonusTermsPage'),
            meta: {
                shouldDisableRGAlerts: true,
                ...allowWithPendingConsents(),
                ...allowWithPendingGracePeriod(),
                ...allowPendingVerification(),
                ...modalGuard(
                    getPagesComponent('BonusTermsPage'),
                    'bonus-terms.title',
                    {
                        uniqueModalId: 'bonus-terms',
                    },
                ),
                ...lightTopNavigation('bonus-terms.title'),
            },
        },
        {
            name: RouteName.PRIVACY_AND_COOKIE_POLICY,
            path: 'tnc/privacy-and-cookie-policy',
            component: getPagesComponent('PrivacyCookiePolicyPage'),
            meta: {
                shouldDisableRGAlerts: true,
                ...allowWithPendingConsents(),
                ...allowWithPendingGracePeriod(),
                ...allowPendingVerification(),
                ...modalGuard(
                    getPagesComponent('PrivacyCookiePolicyPage'),
                    'privacy-and-cookie-policy.title',
                    {
                        uniqueModalId: 'privacyCookiePolicy',
                    },
                ),
                ...lightTopNavigation('privacy-and-cookie-policy.title'),
            },
        },
        {
            name: RouteName.ABOUT_US,
            path: 'about-us',
            component: getPagesComponent('AboutUsPage'),
            meta: {
                ...featureGuard(Feature.ABOUT_US),
                ...lightTopNavigation('about-us.title'),
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
            },
        },
        {
            name: RouteName.RESPONSIBLE_GAMING_PAGE,
            path: 'responsible-gaming',
            component: getPagesComponent('ResponsibleGamingPage'),
            children: [
                {
                    name: RouteName.RESPONSIBLE_GAMING_PAGE,
                    path: ':categorySlug?',
                },
            ],
            meta: {
                shouldDisableRGAlerts: true,
                ...featureGuard(Feature.RESPONSIBLE_GAMING_PAGE),
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
                ...lightTopNavigation('rg.title'),
            },
        },
        {
            name: RouteName.TWO_FACTOR_AUTHENTICATION,
            path: 'two-factor-authentication',
            component: getPagesComponent('TwoFactorAuthenticationPage'),
            meta: {
                shouldDisableRGAlerts: true,
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
                ...modalGuard(
                    getPagesComponent('TwoFactorAuthenticationPage'),
                    'two-factor-auth-page.title',
                    {
                        uniqueModalId: 'two-factor-auth-page',
                        options: {
                            backArrow: true,
                        },
                    },
                ),
                ...lightTopNavigation('two-factor-auth-page.title'),
            },
        },
        {
            name: RouteName.RG_QUESTIONNAIRE,
            path: 'rg-questionnaire',
            component: getRGQuestionnaireComponent('RgQuestionnairePage'),
            meta: {
                ...modalGuard(
                    getRGQuestionnaireComponent('RgQuestionnairePage'),
                    'rg-questionnaire.heading',
                    {
                        desktopOnly: false,
                        options: {
                            size: ModalSize.LARGE,
                            backArrow: true,
                            backArrowContent: 'rg-questionnaire.button.reset',
                            onBack: () => {
                                const event = new CustomEvent(
                                    'reset-questionnaire',
                                );
                                window.dispatchEvent(event);
                            },
                        },
                    },
                ),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.COOKIES,
            path: 'cookies',
            component: getPagesComponent('CookiesPage'),
            meta: {
                shouldDisableRGAlerts: true,
                ...allowWithPendingConsents(),
                ...allowWithPendingGracePeriod(),
                ...allowPendingVerification(),
            },
        },
        {
            name: RouteName.FAQ,
            path: 'faq',
            component: PassThrough,
            children: FaqRoutes,
            meta: {
                shouldDisableRGAlerts: true,
                timedOutAllowed: true,
                ...featureGuard(Feature.FAQS),
                ...allowWithPendingGracePeriod(),
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
                ...lightTopNavigation('faq.page.title'),
            },
        },
        {
            name: RouteName.OFFER,
            path: 'offer/:slug',
            component: getPagesComponent('OfferPage'),
            meta: {
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.SPORTSBOOK,
            path: 'sports/:sports*',
            component: SportsbookPage,
            meta: {
                ...featureGuard(Feature.SPORTSBOOK),
                allowedJurisdictions: [Jurisdiction.ONJN],
            },
        },
        {
            name: RouteName.SHOP,
            path: 'shop',
            component: getGamificationComponent('ShopPage'),
            meta: {
                ...featureGuard(Feature.SHOP),
                ...lightTopNavigation('shop.title'),
                ...authenticationGuard(AuthRestriction.AUTH_REQUIRED),
            },
        },
        {
            name: RouteName.LOTBA_OLD_SELF_EXCLUSION_LINK,
            path: 'external-self-exclusion-lotba',
            beforeEnter: oldLotbaSelfExcludeLink,
            meta: {
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
            },
        },
        {
            name: RouteName.AFFILIATES,
            path: 'affiliates',
            component: getPagesComponent('AffiliatesPage'),
            meta: {
                ...allowPendingVerification(),
                ...allowWithPendingConsents(),
            },
        },
        {
            name: RouteName.PAGE404,
            path: '404',
            component: NotFoundPage,
            meta: {
                shouldDisableRGAlerts: true,
                ...allowWithPendingGracePeriod(),
            },
        },
        {
            path: '*',
            component: NotFoundPage,
            meta: {
                ...allowWithPendingConsents(),
                ...allowWithPendingGracePeriod(),
                ...allowPendingVerification(),
                shouldDisableRGAlerts: true,
            },
        },
    ];

    const routes = singleLanguage
        ? getSingleLanguageRoutes(children)
        : getMultiLanguageRoutes(children);

    router = new Router({
        mode: 'history',
        base: process.env.BASE_URL,
        scrollBehavior: (to, from, savedPosition) => {
            const alwaysScrollToTop = to.meta && to.meta.alwaysScrollToTop;
            const isRedirectedHomeToGames =
                to.name === RouteName.GAMES && from.name === RouteName.HOME;

            // It is for keep the scroll position if there is a redirection from Home to Games
            if (!alwaysScrollToTop && isRedirectedHomeToGames) {
                return;
            }

            if (!alwaysScrollToTop && to.name === from.name) {
                return;
            }

            if (savedPosition) {
                return savedPosition;
            } else {
                return { x: 0, y: 0 };
            }
        },
        routes,
    });

    router.beforeEach(async (to, from, next) => {
        const sportsbookRestrictedRoutes = [
            RouteName.GAMES,
            RouteName.LIVE_CASINO,
            RouteName.FREE_TO_PLAY,
            RouteName.GAMEPLAY,
            RouteName.BINGO_LOBBY,
            RouteName.PROVIDERS,
        ];

        if (
            isSportsbookOnly &&
            sportsbookRestrictedRoutes.includes(to.name as RouteName)
        ) {
            return next('/');
        }

        next();
    });

    if (config.zendesk?.version === 'chat-only') {
        router.beforeEach(async (to, _, next) => {
            if (await isMobileQuery()) {
                if (
                    to.name === RouteName.SPORTSBOOK ||
                    to.name === RouteName.GAMEPLAY
                ) {
                    zendeskChat?.hide?.();
                } else {
                    zendeskChat?.show?.();
                }
            }

            next();
        });
    }

    // Add routing function to the magic config
    setRouteGetter(getRoute.bind(null, router));

    //Initialize route query features toggle
    initCMSFeaturesQuery(router);

    // Initialize Guards.
    if (!singleLanguage) {
        initLanguageGuard(router);
    }
    initFeatureGuard(router);
    initAuthenticationGuard(router);
    initVerificationGuard(router);
    initDepositLimitGuard(router);
    initReturningUserGuard(router);
    initConsentRequiredGuard(router);
    initSowGuard(router);
    initGracePeriodGuard(router);
    initModalGuard(router);
    initNavigationGuard(router);
    initAgcoFirstLoginGuard(router);
    initAglcFirstLoginGuard(router);
    initBettingLimitsGuard(router);
    initAccountLinkingGuard(router);
    initPlayControlGuard(router);
    initResponsibleGamingGuard(router);
    initPCPreferencesGuard(router);
    initSubdivisionGuard(router);
    initVerifyDetailsModalGuard(router);
    initPepGuard(router);
    initAllowedCountries(router);
    initConfirmationLimitsGuard(router);
    initSGASetLimitsGuard(router);
    initSgaIncompleteGuard(router);
    initTimedOutGuard(router, store);

    // initiate affiliate tracking
    router.beforeEach((to, _, next) => {
        if (typeof to.query.btag === 'string' && to.query.btag.length) {
            toolkit.affiliate.setAffiliate(to.query.btag);
        }
        next();
    });

    const unloadListener = async () => {
        await window.eventBus.publish(WINDOW_BEFORE_UNLOAD);
        window.removeEventListener('beforeunload', unloadListener);
    };
    window.addEventListener('beforeunload', unloadListener);

    return router;
}
